/**
 * Dashboard Component
 * 
 * The main landing page for authenticated users that displays:
 * - Welcome hero section
 * - Quick stats (portfolios, projects)
 * - Quick-access shortcuts to create new portfolios/projects
 * - Recent portfolios and projects
 * - Overview of user activity
 * 
 * Features:
 * - Shows user statistics and overview
 * - Quick action buttons for common tasks
 * - Recent items list with links
 * - Responsive Bootstrap layout
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  loadUserPortfolios
} from '../components/BlogSystem/utilities/Portfolios/draftPortfolioUtils';

import { loadUserProjects } from '../components/ProjectsAndSkills/Projects/utilities/projectsUtils';
import { loadUserSkills } from '../components/ProjectsAndSkills/Skills/utilities/skillsUtils';
import { loadUserJobs } from '../components/Jobs/utilities/jobsUtils';
import PageContainer from '../components/PageUtil/PageContainer';
import AppLogo from '../components/Common/Brand/AppLogo';
import FirstPortfolioGuide from '../components/Common/Onboarding/FirstPortfolioGuide';
import { useAuth } from '../components/Auth/context/AuthContext';
import { PORTFOLIO_CONTEXT_ROUTE, PORTFOLIO_DRAFTS_ROUTE } from '../navigationRoutes';

const Dashboard = () => {
  const { user, isAdmin } = useAuth();
  const [userId, setUserId] = useState(null);
  const [userEmail, setUserEmail] = useState('');
  const [portfolios, setPortfolios] = useState([]);
  const [sharedPortfolios, setSharedPortfolios] = useState([]);
  const [projects, setProjects] = useState([]);
  const [skills, setSkills] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  // Get current user
  useEffect(() => {
    if (user) {
      setUserId(user.uid);
      setUserEmail(user.email);
    } else {
      navigate('/login');
    }
  }, [user, navigate]);

  // Load all data
  useEffect(() => {
    if (userId) {
      loadDashboardData();
    }
  }, [userId]);

  const loadDashboardData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [userPort, userProj, userSkills, userJobs] = await Promise.all([
        loadUserPortfolios(userId),
        loadUserProjects(userId),
        loadUserSkills(userId),
        loadUserJobs(userId),
      ]);

      setPortfolios(userPort || []);
      setSharedPortfolios([]);
      setProjects(userProj || []);
      setSkills(userSkills || []);
      setJobs(userJobs || []);
    } catch (err) {
      console.error('Error loading dashboard data:', err);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePortfolio = () => {
    const title = prompt('Enter portfolio title:');
    if (title && title.trim()) {
      const trimmedTitle = title.trim();
      navigate(`/${userId}/drafts/editor/${encodeURIComponent(trimmedTitle)}?new=true`);
    }
  };

  const handleViewPortfolio = (portfolioId) => {
    navigate(`/${userId}/drafts/view/${encodeURIComponent(portfolioId)}`);
  };

  const handleEditPortfolio = (portfolioId) => {
    navigate(`/${userId}/drafts/editor/${encodeURIComponent(portfolioId)}`);
  };

  return (
    <PageContainer error={error} setError={setError} isAdmin={isAdmin}>
      <div className="py-5">
        {/* Welcome Hero Section */}
        <div className="mb-5 px-3 px-md-0">
          <div
            className="bg-gradient p-5 rounded-3 d-flex align-items-center justify-content-between"
            style={{
              background:
                'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              boxShadow: '0 10px 30px rgba(102, 126, 234, 0.4)',
            }}
          >
            <div>
              <h1 className="display-5 fw-bold mb-2">Welcome Back!</h1>
              <p className="lead mb-0 fs-5">
                <i className="bi bi-person-circle me-2"></i>
                {userEmail}
              </p>
            </div>
            <AppLogo className="d-none d-lg-block" size={144} />
          </div>
        </div>

        {loading ? (
          <div className="d-flex justify-content-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : (
          <div className="px-3 px-md-0">
            <FirstPortfolioGuide
              jobCount={jobs.length}
              skillCount={skills.length}
              projectCount={projects.length}
              portfolioCount={portfolios.length}
              onOpenJobs={() => navigate('/projects?tab=jobs')}
              onOpenSkills={() => navigate('/projects?tab=skills')}
              onOpenProjects={() => navigate('/projects?tab=projects')}
              onCreatePortfolio={handleCreatePortfolio}
            />
            <div className="alert alert-primary border-0 shadow-sm mb-4" role="note">
              <h2 className="h5 mb-2">Build a portfolio around your goals</h2>
              <p className="mb-0">
                Add your projects, skills, and desired jobs as portfolio context. The AI uses them to create a
                tailored website that highlights truthful, relevant experience for the roles you want.
              </p>
            </div>
            {/* Stats Cards Section */}
            <div className="row g-3 mb-5">
              <div className="col-md-6 col-lg-4">
                <div className="card h-100 border-0 shadow-sm hover-lift">
                  <div className="card-body text-center py-4">
                    <div className="mb-3">
                      <i
                        className="bi bi-folder2 fs-1"
                        style={{ color: '#667eea' }}
                      ></i>
                    </div>
                    <h3 className="card-title mb-1">
                      {portfolios.length}
                    </h3>
                    <p className="text-muted mb-0">Portfolios</p>
                  </div>
                </div>
              </div>

              <div className="col-md-6 col-lg-4">
                <div className="card h-100 border-0 shadow-sm hover-lift">
                  <div className="card-body text-center py-4">
                    <div className="mb-3">
                      <i
                        className="bi bi-kanban color-primary fs-1"
                      ></i>
                    </div>
                    <h3 className="card-title mb-1">
                      {projects.length}
                    </h3>
                    <p className="text-muted mb-0">Projects</p>
                  </div>
                </div>
              </div>

              <div className="col-md-6 col-lg-4">
                <div className="card h-100 border-0 shadow-sm hover-lift">
                  <div className="card-body text-center py-4">
                    <div className="mb-3">
                      <i
                        className="bi bi-bar-chart color-primary fs-1"
                        
                      ></i>
                    </div>
                    <h3 className="card-title mb-1">
                      {portfolios.length + projects.length}
                    </h3>
                    <p className="text-muted mb-0">Total Items</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Actions Section */}
            <div className="mb-5">
              <h3 className="h4 mb-3 fw-bold">Quick Actions</h3>
              <div className="row g-3">
                <div className="col-sm-6 col-lg-4">
                  <button
                    className="btn btn-primary btn-lg w-100 py-3 rounded-2"
                    onClick={handleCreatePortfolio}
                  >
                    <i className="bi bi-plus-circle me-2 fs-5"></i>
                    <div>Create Portfolio</div>
                  </button>
                </div>
                <div className="col-sm-6 col-lg-4">
                  <button
                    className="btn btn-info btn-lg w-100 py-3 rounded-2"
                    onClick={() => navigate(PORTFOLIO_DRAFTS_ROUTE)}
                  >
                    <i className="bi bi-eye me-2 fs-5"></i>
                    <div>View Portfolios</div>
                  </button>
                </div>
                <div className="col-sm-6 col-lg-4">
                  <button
                    className="btn btn-info btn-lg w-100 py-3 rounded-2"
                    onClick={() => navigate(PORTFOLIO_CONTEXT_ROUTE)}
                  >
                    <i className="bi bi-list-check me-2 fs-5"></i>
                    <div>Manage Portfolio Context</div>
                  </button>
                </div>
              </div>
            </div>

            {/* Recent Items Section */}
            <div className="row g-4">
              {/* Recent Portfolios */}
              <div className="col-lg-6">
                <h3 className="h4 mb-3 fw-bold">Recent Portfolios</h3>
                {portfolios.length === 0 ? (
                  <div className="alert alert-info mb-0 rounded-2">
                    <i className="bi bi-info-circle me-2"></i>
                    No portfolios yet.{' '}
                    <a
                      href="#!"
                      onClick={(e) => {
                        e.preventDefault();
                        handleCreatePortfolio();
                      }}
                      className="alert-link"
                    >
                      Create one now!
                    </a>
                  </div>
                ) : (
                  <div className="list-group list-group-flush">
                    {portfolios.slice(0, 5).map((portfolio) => (
                      <div
                        key={portfolio.id}
                        className="list-group-item px-0 py-3 border-bottom"
                      >
                        <div className="d-flex justify-content-between align-items-start">
                          <div className="flex-grow-1">
                            <h5 className="mb-1">
                              <i className="bi bi-folder me-2"></i>
                              {portfolio.title}
                            </h5>
                            <small className="text-muted">
                              Updated:{' '}
                              {new Date(
                                portfolio.updatedAt?.toDate?.() ||
                                portfolio.updatedAt
                              ).toLocaleDateString()}
                            </small>
                          </div>
                          <div className="btn-group btn-group-sm ms-2">
                            <button
                              className="btn btn-outline-primary"
                              onClick={() =>
                                handleViewPortfolio(portfolio.id)
                              }
                              title="View"
                            >
                              <i className="bi bi-eye"></i>
                            </button>
                            <button
                              className="btn btn-outline-primary"
                              onClick={() =>
                                handleEditPortfolio(portfolio.id)
                              }
                              title="Edit"
                            >
                              <i className="bi bi-pencil"></i>
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Recent Projects */}
              <div className="col-lg-6">
                <h3 className="h4 mb-3 fw-bold">Recent Projects</h3>
                {projects.length === 0 ? (
                  <div className="alert alert-info mb-0 rounded-2">
                    <i className="bi bi-info-circle me-2"></i>
                    No projects yet.{' '}
                    <a
                      href="#!"
                      onClick={(e) => {
                        e.preventDefault();
                        navigate('/projects?tab=projects');
                      }}
                      className="alert-link"
                    >
                      Add one now!
                    </a>
                  </div>
                ) : (
                  <div className="list-group list-group-flush">
                    {projects.slice(0, 5).map((project) => (
                      <div
                        key={project.id}
                        className="list-group-item px-0 py-3 border-bottom"
                      >
                        <div className="justify-content-between align-items-start w-100">

                          <h5 className="mb-1">
                            <i className="bi bi-kanban me-2"></i>
                            {project.title}
                          </h5>
                          {project.overview && (
                            <small className="text-muted d-block text-truncate">
                              {project.overview}
                            </small>
                          )}
                        </div>

                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </PageContainer>
  );
};

export default Dashboard;
